import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Bell, User, Plus, ChevronDown, Menu, X, LogOut, Settings, LayoutDashboard, Globe } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useLanguage } from '../../context/LanguageContext';
import { getRoleBadgeInfo } from '../../utils/formatters';
import { CategoryIcon } from '../../data/categoryIcons';
import { categoryService } from '../../services/categoryService';

const LANG_FLAGS = { tr: '🇹🇷', de: '🇩🇪', en: '🇬🇧' };

export default function Header() {
  const { user, logout, isAdmin, isModerator } = useAuth();
  const { unreadCount, notifications, markRead, markAllRead } = useNotifications();
  const { t, tCat, lang, setLang, supported } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchRef = useRef(null);
  const notifsRef = useRef(null);
  const userRef = useRef(null);
  const categoriesRef = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    categoryService.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifsRef.current && !notifsRef.current.contains(e.target)) setShowNotifs(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserMenu(false);
      if (categoriesRef.current && !categoriesRef.current.contains(e.target)) setShowCategories(false);
      if (langRef.current && !langRef.current.contains(e.target)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const notifIcons = { price_drop: '📉', follow: '👤', question: '💬', reservation: '🔒' };

  return (
    <header className="glass-nav sticky top-0 z-50">
      {/* Top bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 bg-apple-blue rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">B</span>
          </div>
          <span className="font-semibold text-apple-gray-dark text-base tracking-tight hidden sm:block">Biet Es Runter</span>
        </Link>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-apple-gray-mid w-4 h-4" />
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('header.search_placeholder')}
              className="w-full pl-9 pr-4 py-2 rounded-full bg-apple-gray text-sm text-apple-gray-dark placeholder-apple-gray-mid focus:outline-none focus:ring-2 focus:ring-apple-blue/30 transition-all"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Categories dropdown */}
          <div className="relative hidden md:block" ref={categoriesRef}>
            <button
              onClick={() => setShowCategories(v => !v)}
              className="nav-link flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-apple-gray"
            >
              {t('header.categories')} <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showCategories && (
              <div className="absolute top-full mt-2 left-0 bg-white rounded-2xl shadow-apple-md border border-gray-100 p-4 w-72 grid grid-cols-2 gap-2 animate-fade-in z-50">
                {categories.map(cat => (
                  <Link
                    key={cat.id}
                    to={`/search?category=${cat.slug}`}
                    onClick={() => setShowCategories(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-apple-gray text-sm text-apple-gray-dark transition-colors group"
                  >
                    <span className="text-apple-gray-mid group-hover:text-apple-blue transition-colors">
                      <CategoryIcon id={cat.slug} className="w-4 h-4" />
                    </span>
                    {tCat(cat.slug)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Post listing */}
          {user && (
            <Link to="/create" className="btn-primary hidden sm:flex items-center gap-1.5 text-sm">
              <Plus className="w-4 h-4" /> {t('header.post_listing')}
            </Link>
          )}

          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setShowLangMenu(v => !v)}
              className="flex items-center gap-1.5 p-2 rounded-full hover:bg-apple-gray transition-colors text-sm text-apple-gray-dark"
              title="Language / Sprache / Dil"
            >
              <Globe className="w-4 h-4 text-apple-gray-mid" />
              <span className="hidden sm:inline font-medium">{LANG_FLAGS[lang]}</span>
            </button>
            {showLangMenu && (
              <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-apple-md border border-gray-100 overflow-hidden animate-fade-in z-50 w-36">
                {supported.map(code => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setShowLangMenu(false); }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-apple-gray transition-colors ${lang === code ? 'font-semibold text-apple-blue' : 'text-apple-gray-dark'}`}
                  >
                    <span>{LANG_FLAGS[code]}</span>
                    <span>{{ tr: 'Türkçe', de: 'Deutsch', en: 'English' }[code]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notifications */}
          {user && (
            <div className="relative" ref={notifsRef}>
              <button
                onClick={() => setShowNotifs(v => !v)}
                className="relative p-2 rounded-full hover:bg-apple-gray transition-colors"
              >
                <Bell className="w-5 h-5 text-apple-gray-dark" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-apple-red rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-apple-md border border-gray-100 w-80 overflow-hidden animate-fade-in z-50">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm text-apple-gray-dark">{t('header.notifications')}</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-apple-blue hover:underline">{t('header.mark_all_read')}</button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-center text-apple-gray-mid text-sm py-8">{t('header.no_notifications')}</p>
                    ) : notifications.map(n => (
                      <button
                        key={n.id}
                        onClick={() => { markRead(n.id); if (n.listingId) navigate(`/listing/${n.listingId}`); setShowNotifs(false); }}
                        className={`w-full text-left px-4 py-3 hover:bg-apple-gray transition-colors flex items-start gap-3 ${!n.read ? 'bg-blue-50/50' : ''}`}
                      >
                        <span className="text-lg leading-none mt-0.5">{notifIcons[n.type] || '🔔'}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!n.read ? 'font-medium text-apple-gray-dark' : 'text-apple-gray-mid'}`}>{n.message}</p>
                          <p className="text-xs text-apple-gray-mid mt-0.5">{n.time}</p>
                        </div>
                        {!n.read && <span className="w-2 h-2 bg-apple-blue rounded-full mt-1.5 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User menu */}
          {user ? (
            <div className="relative" ref={userRef}>
              <button
                onClick={() => setShowUserMenu(v => !v)}
                className="flex items-center gap-2 p-1.5 rounded-full hover:bg-apple-gray transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-apple-blue flex items-center justify-center">
                  <span className="text-white text-xs font-semibold">{user.name?.[0]?.toUpperCase()}</span>
                </div>
              </button>
              {showUserMenu && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-apple-md border border-gray-100 w-52 overflow-hidden animate-fade-in z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-sm text-apple-gray-dark truncate">{user.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${getRoleBadgeInfo(user.role).className}`}>
                      {getRoleBadgeInfo(user.role).label}
                    </span>
                  </div>
                  <div className="py-1">
                    <Link to={`/profile/${user.id}`} onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-apple-gray text-sm text-apple-gray-dark transition-colors">
                      <User className="w-4 h-4" /> {t('header.my_profile')}
                    </Link>
                    <Link to="/my-listings" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-apple-gray text-sm text-apple-gray-dark transition-colors">
                      <LayoutDashboard className="w-4 h-4" /> {t('header.my_listings')}
                    </Link>
                    {(isAdmin || isModerator) && (
                      <Link to="/admin" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-apple-gray text-sm text-apple-red transition-colors">
                        <Settings className="w-4 h-4" /> {t('header.admin_panel')}
                      </Link>
                    )}
                  </div>
                  <div className="border-t border-gray-100 py-1">
                    <button onClick={() => { logout(); setShowUserMenu(false); navigate('/'); }} className="flex items-center gap-3 px-4 py-2.5 hover:bg-apple-gray text-sm text-apple-red transition-colors w-full">
                      <LogOut className="w-4 h-4" /> {t('header.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" className="nav-link hidden sm:block px-3 py-1.5">{t('header.login')}</Link>
              <Link to="/register" className="btn-primary text-sm">{t('header.register')}</Link>
            </div>
          )}

          {/* Mobile menu */}
          <button onClick={() => setMobileOpen(v => !v)} className="p-2 rounded-full hover:bg-apple-gray md:hidden">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white py-4 px-4 animate-slide-up">
          <div className="grid grid-cols-3 gap-2 mb-4">
            {categories.slice(0, 9).map(cat => (
              <Link
                key={cat.id}
                to={`/search?category=${cat.slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-apple-gray text-xs text-apple-gray-dark group"
              >
                <div className="w-10 h-10 rounded-xl bg-apple-gray group-hover:bg-apple-blue group-hover:text-white text-apple-gray-mid flex items-center justify-center transition-all">
                  <CategoryIcon id={cat.slug} className="w-4 h-4" />
                </div>
                {tCat(cat.slug)}
              </Link>
            ))}
          </div>
          {/* Mobile lang switcher */}
          <div className="flex gap-2 justify-center mb-3">
            {supported.map(code => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${lang === code ? 'bg-apple-blue text-white border-apple-blue' : 'border-gray-200 text-apple-gray-mid'}`}
              >
                {LANG_FLAGS[code]} {{ tr: 'TR', de: 'DE', en: 'EN' }[code]}
              </button>
            ))}
          </div>
          {user && (
            <Link to="/create" onClick={() => setMobileOpen(false)} className="btn-primary w-full flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> {t('header.post_listing')}
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
